import { useAuthContext, useIsHydrated } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Button, ErrorMessage, Input, LoaderDots } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { MouseEvent, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { usePasswordValidator, useValidations, useSubmitValidation } from '../../..';
import { AccountWizardState } from '../../../types';
import { PasswordEyeIcon } from '../../atoms';

interface Props {
    emailAddress?: string;
    onAuthorized?: () => void;
    onBack: () => void;
}

export const ForgotPassword = ({ emailAddress, onAuthorized, onBack }: Props) => {
    const { t } = useTranslation();
    const {
        register,
        formState: { errors },
        getValues,
        handleSubmit,
        trigger,
        watch
    } = useForm<AccountWizardState>({ mode: 'onSubmit', reValidateMode: 'onSubmit', defaultValues: { emailAddress } });
    const validate = useSubmitValidation({ watch, trigger });
    const onSubmit: SubmitHandler<AccountWizardState> = () => {};

    const [step, setStep] = useState<'forgot-password-email' | 'forgot-password'>(emailAddress ? 'forgot-password' : 'forgot-password-email');
    const [passwordType, setPasswordType] = useState('password');
    const [passwordScore, setPasswordScore] = useState(-1);

    const { updateAuthState } = useAuthContext();
    const isHydrated = useIsHydrated();
    const { validatePassword, passwordValidation, passwordMessages } = usePasswordValidator();
    const { emailValidation, passwordCodeValidation } = useValidations();

    const togglePasswordVisibility = () => setPasswordType((prev) => (prev === 'password' ? 'text' : 'password'));


    const currentEmail = watch('emailAddress');
    const password = watch('newPassword');
    useEffect(() => {
        if (password) {
            validatePassword(password).then((result) => setPasswordScore(result.score));
        } else {
            setPasswordScore(-1);
        }
    }, [password]);

    const forgotPasswordMutation = useMutation(OptionsApi.login.forgotPassword());

    useEffect(() => {
        if (emailAddress) {
            forgotPasswordMutation.mutate({ emailAddress });
        }
    }, []);

    const sendForgotPasswordEmail = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        validate(['emailAddress']).then((isValid) => {
            if (!isValid) return;
            const { emailAddress } = getValues();
            if (!emailAddress) return;
            setStep('forgot-password');
            forgotPasswordMutation.mutate({ emailAddress });
        });
    };

    const resetPasswordMutation = useMutation(OptionsApi.login.resetPassword());
    const resetPassword = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        validate(['forgotPasswordCode', 'newPassword']).then((isValid) => {
            if (!isValid) return;
            const { forgotPasswordCode, newPassword } = getValues();
            resetPasswordMutation.mutate(
                { linkId: forgotPasswordCode, password: newPassword },
                {
                    onSuccess: (response) => {
                        updateAuthState(response);
                        if (onAuthorized) onAuthorized();
                    }
                }
            );
        });
    };

    const handleBack = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onBack();
    };

    const error = forgotPasswordMutation.error ?? resetPasswordMutation.error;
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {error && <ErrorMessage errorSubject="Account.ApiErrorCode" apiError={error} classNames="mb-4" />}
            {step === 'forgot-password-email' && (
                <>
                    <Input
                        autoFocus
                        type="email"
                        label={t('Account.Label.Email')}
                        {...register('emailAddress', emailValidation)}
                        errorMessage={errors.emailAddress?.message}
                    />
                    <div className="clear-both flex flex-wrap gap-4 clear-both mb-8">
                        <Button type="button" isLoading={forgotPasswordMutation.isPending} onClick={handleBack} variant="primary-outline" classNames="flex-1 whitespace-nowrap">
                            {t('Account.Button.Back')}
                        </Button>
                        <Button type="submit" isLoading={forgotPasswordMutation.isPending} onClick={sendForgotPasswordEmail} variant="primary" classNames="flex-1 whitespace-nowrap">
                            {t('Account.Button.Next')}
                        </Button>
                    </div>
                </>
            )}
            {step === 'forgot-password' && (
                <>
                    {forgotPasswordMutation.isPending && (
                        <p className="animate-pulse">
                            <Trans i18nKey="Account.Message.SendingEmail" values={{ email: currentEmail }} />
                            <LoaderDots classNames="relative bottom-[-.17em] h-0.75em inline-block fill-current ms-1" />
                        </p>
                    )}
                    {!forgotPasswordMutation.isPending && forgotPasswordMutation.isError && (
                        <p>
                            <Trans i18nKey="Account.Message.ErrorSendingEmail" values={{ email: currentEmail }} />
                        </p>
                    )}
                    {!forgotPasswordMutation.isPending && !forgotPasswordMutation.isError && (
                        <>
                            <p className="mb-8">
                                <Trans i18nKey="Account.Message.EmailSent" values={{ email: currentEmail }} />
                            </p>
                            <Input
                                autoFocus
                                label={t('Account.Label.ForgotPasswordCode')}
                                {...register('forgotPasswordCode', passwordCodeValidation)}
                                errorMessage={errors.forgotPasswordCode?.message}
                                autoComplete="off"
                            />
                            <Input
                                type={passwordType}
                                label={t('Account.Label.NewPassword')}
                                {...register('newPassword', passwordValidation)}
                                {...passwordMessages(passwordScore, errors.newPassword?.message)}
                                autoComplete="new-password"
                            >
                                <PasswordEyeIcon passwordType={passwordType} onClick={togglePasswordVisibility} />
                            </Input>
                            <div className="clear-both flex flex-wrap gap-4 clear-both mb-8">
                                <Button type="button" isLoading={resetPasswordMutation.isPending} onClick={handleBack} variant="primary-outline" classNames="flex-1 whitespace-nowrap">
                                    {t('Account.Button.Back')}
                                </Button>
                                <Button
                                    data-testid="forgot-password-submit"
                                    type="submit"
                                    isLoading={resetPasswordMutation.isPending}
                                    disabled={!isHydrated}
                                    onClick={resetPassword}
                                    variant="primary"
                                    classNames="flex-1 whitespace-nowrap"
                                >
                                    {t('Account.Button.ResetPassword')}
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}
        </form>
    );
};
